import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";

import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  grayPrimaryFontColor,
} from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Helper ───────────────────────────────────────────────────────────────────

const DetailTile = ({ label, value }) => (
  <Box sx={{ backgroundColor: dashboardCardBgColor, borderRadius: "8px", px: 1.5, py: 1.25 }}>
    <Typography
      sx={{
        ...typography.bodySmall,
        color: grayFontColor,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100 }}>
      {value || "—"}
    </Typography>
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

const AdvisorCallModal = ({ open, row, onClose, onComplete }) => {
  const [note, setNote]   = useState("");
  const [done, setDone]   = useState(false);

  useEffect(() => {
    if (row) {
      setNote(row.note || "");
      setDone(false);
    }
  }, [row?.id]);

  if (!row) return null;

  const handleComplete = () => {
    setDone(true);
    setTimeout(() => {
      onComplete({ ...row, note });
    }, 1800);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: ColorsConstants.primaryBackgroundColor,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneInTalkOutlinedIcon sx={{ color: ColorsConstants.primaryWhite, fontSize: 20 }} />
            <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryWhite }}>
              Advisor Call Request
            </Typography>
          </Box>
          <Typography sx={{ ...typography.bodySmall, color: "rgba(255,255,255,0.5)", mt: 0.25 }}>
            View details and mark as completed
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: ColorsConstants.primaryWhite,
            backgroundColor: "rgba(255,255,255,0.12)",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>

        {/* Detail tiles */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6}><DetailTile label="Client Name" value={row.client_name}     /></Grid>
          <Grid item xs={6}><DetailTile label="Phone"       value={row.client_phone_no} /></Grid>
          <Grid item xs={6}><DetailTile label="PAN"         value={row.pan}             /></Grid>
          <Grid item xs={6}><DetailTile label="Client Code" value={row.client_code}     /></Grid>
          <Grid item xs={6}><DetailTile label="Request"     value={row.type}            /></Grid>
          <Grid item xs={6}><DetailTile label="Status"      value={row.status}          /></Grid>
          <Grid item xs={12}><DetailTile label="Detail"     value={row.detail}          /></Grid>
          <Grid item xs={12}>
            <DetailTile
              label="Requested At"
              value={row.created_at ? formatWithTzLabel(row.created_at) : "—"}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2.5 }} />

        {/* Editable note */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              ...typography.bodySmall,
              color: grayFontColor,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 0.75,
              fontWeight: 600,
            }}
          >
            Note (editable)
          </Typography>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add or update note for this call…"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                ...typography.bodyLarge,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: borderColors.cardBorderPrimaryGrayColor },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryPurple100 },
              },
              "& .MuiInputBase-input": { ...typography.bodyLarge, color: grayPrimaryFontColor },
            }}
          />
        </Box>

        {done && (
          <Alert
            icon={<CheckCircleOutlineIcon />}
            severity="success"
            sx={{ borderRadius: "8px", ...typography.bodySmall }}
          >
            Call marked as completed for <strong>{row.client_name}</strong>.
          </Alert>
        )}

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          gap: 1,
          borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          mt: 1,
        }}
      >
        <ButtonTwo onClick={onClose} disabled={done}>Cancel</ButtonTwo>
        <ButtonOne onClick={handleComplete} disabled={done}>
          {done ? "Completing…" : "Mark as Completed"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default AdvisorCallModal;
